import React, { useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import PlaceIcon from '@mui/icons-material/Place'

interface LocationData {
  id: string
  name: string
  address?: string
  total_profile_views: number
  total_phone_reveals: number
  total_email_reveals: number
  total_website_clicks: number
  // Trends (vs previous period)
  trend_views?: number
  trend_phone?: number
}

interface LocationPerformanceTableProps {
  locations: LocationData[]
  onLocationSelect?: (locationId: string) => void
}

type SortKey = 'name' | 'total_profile_views' | 'total_phone_reveals' | 'total_email_reveals' | 'conversion'
type SortDirection = 'asc' | 'desc'

export default function LocationPerformanceTable({
  locations,
  onLocationSelect,
}: LocationPerformanceTableProps) {
  const [sortBy, setSortBy] = useState<SortKey>('total_profile_views')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Calculate conversion rate (phone + email reveals / views)
  const getConversionRate = (loc: LocationData) => {
    if (loc.total_profile_views === 0) return 0
    return ((loc.total_phone_reveals + loc.total_email_reveals) / loc.total_profile_views) * 100
  }

  // Sort locations
  const sortedLocations = [...locations].sort((a, b) => {
    let aValue: number | string
    let bValue: number | string

    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase()
        bValue = b.name.toLowerCase()
        break
      case 'conversion':
        aValue = getConversionRate(a)
        bValue = getConversionRate(b)
        break
      default:
        aValue = a[sortBy] || 0
        bValue = b[sortBy] || 0
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    }
  })

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDirection('desc')
    }
  }

  // Trend indicator component
  const TrendIndicator = ({ value }: { value?: number }) => {
    if (value === undefined || value === 0) {
      return (
        <Tooltip title="Ingen ændring">
          <TrendingFlatIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5 }} />
        </Tooltip>
      )
    }
    if (value > 0) {
      return (
        <Tooltip title={`+${value}% vs. forrige periode`}>
          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', ml: 0.5 }} />
        </Tooltip>
      )
    }
    return (
      <Tooltip title={`${value}% vs. forrige periode`}>
        <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', ml: 0.5 }} />
      </Tooltip>
    )
  }

  // Calculate totals for summary row
  const totals = locations.reduce(
    (acc, loc) => ({
      views: acc.views + loc.total_profile_views,
      phone: acc.phone + loc.total_phone_reveals,
      email: acc.email + loc.total_email_reveals,
      website: acc.website + loc.total_website_clicks,
    }),
    { views: 0, phone: 0, email: 0, website: 0 }
  )

  const totalConversion = totals.views > 0 
    ? ((totals.phone + totals.email) / totals.views) * 100 
    : 0

  if (locations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'background.default', borderRadius: 2 }}>
        <Typography color="text.secondary">Ingen lokationsdata tilgængelig</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Performance per lokation
      </Typography>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Lokation
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'total_profile_views'}
                  direction={sortBy === 'total_profile_views' ? sortDirection : 'desc'}
                  onClick={() => handleSort('total_profile_views')}
                >
                  Visninger
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'total_phone_reveals'}
                  direction={sortBy === 'total_phone_reveals' ? sortDirection : 'desc'}
                  onClick={() => handleSort('total_phone_reveals')}
                >
                  Telefon vist
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === 'total_email_reveals'}
                  direction={sortBy === 'total_email_reveals' ? sortDirection : 'desc'}
                  onClick={() => handleSort('total_email_reveals')}
                >
                  Email vist
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Andel af besøgende der viser telefon eller email">
                  <TableSortLabel
                    active={sortBy === 'conversion'}
                    direction={sortBy === 'conversion' ? sortDirection : 'desc'}
                    onClick={() => handleSort('conversion')}
                  >
                    Konvertering
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLocations.map((loc, index) => {
              const conversion = getConversionRate(loc)
              const isTopPerformer = index === 0 && sortBy === 'conversion' && sortDirection === 'desc'
              const isLowPerformer = conversion < totalConversion * 0.5 && totals.views > 10
              
              return (
                <TableRow
                  key={loc.id}
                  hover
                  onClick={() => onLocationSelect?.(loc.id)}
                  sx={{ 
                    cursor: onLocationSelect ? 'pointer' : 'default',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {loc.name}
                          </Typography>
                          {isTopPerformer && (
                            <Chip 
                              label="Top" 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                backgroundColor: 'success.light',
                                color: 'success.dark',
                              }} 
                            />
                          )}
                          {isLowPerformer && (
                            <Chip 
                              label="Lav" 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                backgroundColor: 'warning.light',
                                color: 'warning.dark',
                              }} 
                            />
                          )}
                        </Box>
                        {loc.address && (
                          <Typography variant="caption" color="text.secondary">
                            {loc.address}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2">{loc.total_profile_views}</Typography>
                      <TrendIndicator value={loc.trend_views} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2">{loc.total_phone_reveals}</Typography>
                      <TrendIndicator value={loc.trend_phone} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{loc.total_email_reveals}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      color={conversion >= totalConversion ? 'success.main' : 'text.primary'}
                    >
                      {conversion.toFixed(1)}%
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
            
            {/* Summary row */}
            <TableRow sx={{ backgroundColor: 'primary.50', '& td': { fontWeight: 600 } }}>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  Total ({locations.length} lokationer)
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600}>{totals.views}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600}>{totals.phone}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600}>{totals.email}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600}>
                  {totalConversion.toFixed(1)}%
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" color="text.secondary">
            Bedre end forrige periode
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
          <Typography variant="caption" color="text.secondary">
            Dårligere end forrige periode
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip label="Top" size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: 'success.light' }} />
          <Typography variant="caption" color="text.secondary">
            Højeste konvertering
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
